// Componente riutilizzabile per mostrare gli scaglioni PFA con stato,
// avanzamento e mancante. Usato da CustomerHome (compact), CustomerPfaPage
// e CustomerContractsPage (full).
import React from "react";
import { Badge } from "../design-system";

const fmt = (v) => Number(v).toLocaleString("it-IT", {
  minimumFractionDigits: 0, maximumFractionDigits: 3,
});
const fmtPct = (v) => Number(v).toLocaleString("it-IT", {
  minimumFractionDigits: 2, maximumFractionDigits: 3,
});

function statusMeta(status) {
  if (status === "REACHED") return { label: "Raggiunto", tone: "success" };
  if (status === "CURRENT") return { label: "In corso", tone: "info" };
  return { label: "Da raggiungere", tone: "neutral" };
}

/** Compact view for Home card — small list with status badges. */
export function PfaTiersCompact({ tiers, maturedKg, testidPrefix = "pfa-tier" }) {
  if (!tiers || tiers.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }} data-testid={`${testidPrefix}s-compact`}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", marginBottom: 4 }}>
        Scaglioni PFA
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {tiers.map((t) => {
          const meta = statusMeta(t.status);
          return (
            <li key={t.index} data-testid={`${testidPrefix}-${t.index}`}
                style={{ display: "grid",
                         gridTemplateColumns: "auto 1fr auto",
                         alignItems: "center", gap: 8,
                         padding: "5px 0",
                         borderBottom: "1px dashed #eef2f7", fontSize: 12 }}>
              <div>
                <strong>Scaglione {t.index}</strong>
                {" — "}
                <span>{fmt(t.threshold_kg)} kg · {fmtPct(t.reward_percent)}%</span>
              </div>
              <div style={{ color: "#64748b" }}>
                {t.status === "REACHED"
                  ? `${fmt(t.threshold_kg)} / ${fmt(t.threshold_kg)} kg`
                  : t.status === "CURRENT"
                  ? `${fmt(maturedKg)} / ${fmt(t.threshold_kg)} kg — mancano ${fmt(t.remaining_kg)} kg`
                  : `mancano ${fmt(t.remaining_kg)} kg`}
              </div>
              <Badge tone={meta.tone} data-testid={`${testidPrefix}-${t.index}-status`}>
                {meta.label}
              </Badge>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Full table for detail pages. */
export function PfaTiersTable({ tiers, maturedKg, testidPrefix = "pfa-tier" }) {
  if (!tiers || tiers.length === 0) {
    return <div className="muted" style={{ fontSize: 13 }}>Nessuno scaglione configurato.</div>;
  }
  return (
    <div>
      <table className="ds-table" style={{ fontSize: 13, width: "100%" }}
             data-testid={`${testidPrefix}s-table`}>
        <thead><tr>
          <th style={{ textAlign: "left" }}>Scaglione</th>
          <th>Soglia</th>
          <th>Premio</th>
          <th>Stato</th>
          <th>Avanzamento</th>
          <th>Mancano</th>
        </tr></thead>
        <tbody>
          {tiers.map((t) => {
            const meta = statusMeta(t.status);
            const progress = t.status === "REACHED"
              ? `${fmt(t.threshold_kg)} / ${fmt(t.threshold_kg)} kg`
              : t.status === "CURRENT"
              ? `${fmt(maturedKg)} / ${fmt(t.threshold_kg)} kg`
              : "—";
            return (
              <tr key={t.index} data-testid={`${testidPrefix}-row-${t.index}`}>
                <td><strong>{t.index}</strong></td>
                <td>{fmt(t.threshold_kg)} kg</td>
                <td>{fmtPct(t.reward_percent)}%</td>
                <td><Badge tone={meta.tone}>{meta.label}</Badge></td>
                <td>{progress}</td>
                <td>{t.status === "REACHED" ? "—" : `${fmt(t.remaining_kg)} kg`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Segmented progress bar with tier markers */}
      <PfaTiersSegmentedBar tiers={tiers} maturedKg={maturedKg}
                            testidPrefix={testidPrefix} />
    </div>
  );
}

function PfaTiersSegmentedBar({ tiers, maturedKg, testidPrefix }) {
  if (!tiers || tiers.length === 0) return null;
  const maxKg = Math.max(...tiers.map((t) => t.threshold_kg));
  const pct = Math.min(100, Math.max(0, (maturedKg / maxKg) * 100));
  return (
    <div style={{ marginTop: 14 }} data-testid={`${testidPrefix}s-segbar`}>
      <div style={{ position: "relative", height: 10,
                    background: "#e5e7eb", borderRadius: 5, overflow: "visible" }}>
        <div style={{ position: "absolute", top: 0, left: 0, height: "100%",
                      width: `${pct}%`, background: "#0F4C81",
                      borderRadius: 5, transition: "width .4s" }} />
        {tiers.map((t) => {
          const mp = Math.min(100, (t.threshold_kg / maxKg) * 100);
          return (
            <div key={t.index}
                 title={`Scaglione ${t.index} — ${fmt(t.threshold_kg)} kg · ${fmtPct(t.reward_percent)}%`}
                 style={{ position: "absolute", top: -2, left: `calc(${mp}% - 2px)`,
                          width: 4, height: 14,
                          background: t.reached ? "#10b981" : "#94a3b8",
                          borderRadius: 2 }} />
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between",
                    fontSize: 10, color: "#64748b", marginTop: 4 }}>
        <span>0 kg</span>
        {tiers.map((t) => (
          <span key={t.index}
                style={{ color: t.reached ? "#059669" : "#64748b",
                         fontWeight: t.status === "CURRENT" ? 700 : 400 }}>
            {fmt(t.threshold_kg)}
          </span>
        ))}
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: "#334155" }}>
        Attuale: <strong>{fmt(maturedKg)} kg</strong>
      </div>
    </div>
  );
}
